import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { deleteMediaObject, MAX_UPLOAD_BYTES } from '../utils/mediaApi';
import CommentsInline from '../components/CommentsInline';
import ReportModal from '../components/ReportModal';
import TopNavigation from '../components/TopNavigation';
import ImageWithFallback from '../components/ImageWithFallback';
import VerifiedBadge from '../components/VerifiedBadge';
import LeftNavSidebar from '../components/LeftNavSidebar';
import RightWidgetsSidebar from '../components/RightWidgetsSidebar';
import { validateContent } from '../utils/contentModeration';
import { isVideoFile } from '../utils/videoCompression';
import { getProfileUrl } from '../utils/urls';
import MediaBox from '../components/MediaBox';
import PostMediaGallery, { hasMultiMedia, SelectedMediaThumb } from '../components/PostMediaGallery';
import { extractHashtags, extractMentions, renderTextWithLinks } from '../utils/text';
import { uploadModeratedMedia } from '../utils/moderatedUpload';
import { describeModerationError, notifyIfHeldForReview } from '../utils/moderationErrors';
import { createNotification } from '../utils/notificationHelpers';
import AdSlot from '../components/AdSlot';
import { trackContentView, trackInteraction } from '../utils/activityTracking';
import '../styles/home.css';
import StoriesRail from '../components/StoriesRail';
import StoriesViewer, { UserStories, StoryDoc as StoryFull } from '../components/StoriesViewer';
import ReelEditorV2 from '../components/ReelEditorV2';
import MusicLibraryPicker, { MusicTrack as PickerMusicTrack } from '../components/MusicLibraryPicker';
import SignedAudio from '../components/SignedAudio';
import AutoplayPreview from '../components/AutoplayPreview';
import ThumbnailPicker, { uploadThumbnail } from '../components/ThumbnailPicker';
import { compressImage } from '../utils/imageCompression';
import { getGlobalMuted, onGlobalMuteChange } from '../utils/audioPrefs';
import {
  createPost,
  deletePost as deletePostApi,
  getSettings,
  getUserById,
  getUserByUsername,
  getUserMe,
  likePost as likePostApi,
  listFollowedPages,
  listFollowing,
  listLiveStreams,
  listPosts,
  listPulse,
  listSaved,
  listStories,
  listTestimonies,
  saveItem,
  unlikePost as unlikePostApi,
  unsaveItem,
  updatePost as updatePostApi,
} from '../utils/secureFunctions';
import { getMyPhoto, useMyPhoto } from '../utils/myProfile';
import UserLink from '../components/UserLink';
import { appConfirm } from '../utils/dialog';

interface Post {
  id: string;
  userId: string;
  userName: string;
  username?: string;
  userPhoto: string;
    userVerified?: boolean;
  content: string;
  imageURL?: string;
  videoURL?: string;
  thumbnailURL?: string | null;
  media?: { url: string; type: 'image' | 'video' }[];
  bgMusic?: { id: string; title: string; artistId: string; artistName: string; audioURL?: string } | null;
  likeCount: number;
  likedByMe?: boolean;
  comments: number;
  createdAt: any;
  mediaPath?: string;
  ownerType?: 'user' | 'page';
  pageId?: string;
  pageName?: string;
  pagePhoto?: string;
  // Repost support
  type?: 'repost' | 'live_repost' | 'recording_repost';
  repostOf?: string;
  original?: {
    id: string;
    userId: string;
    userName: string;
    userPhoto: string;
    userVerified?: boolean;
    content: string;
    imageURL?: string;
    videoURL?: string;
    createdAt: any;
  };
  // Live stream repost snapshot fields
  originalStreamId?: string;
  streamThumbnail?: string;
  streamRefPath?: string;
  title?: string; // stream title for live reposts
}

interface TrendingItem {
  id: string;
  type: 'pulse' | 'testimony';
  userId: string;
  userName: string;
  userPhoto: string;
    userVerified?: boolean;
  title: string;
  caption?: string;
  videoURL?: string;
  thumbnailURL?: string | null;
  imageURL?: string;
  likes: string[];
  views?: number;
  score: number;
}

interface StoryDoc {
  id: string;
  userId: string;
  userName: string;
  userPhoto: string;
  mediaURL: string;
  mediaType: 'image' | 'video';
  createdAt?: any;
  expiresAt?: any;
}

interface LiveStream {
  id: string;
  userId: string;
  userName: string;
  userPhoto: string;
  title: string;
  thumbnailURL: string;
  viewers: number;
  isLive: boolean;
  recordedURL?: string;
}

export default function Home() {
  const myPhoto = useMyPhoto();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    try { return window.innerWidth <= 768; } catch { return false; }
  });

  useEffect(() => {
    const onResize = () => { try { setIsMobile(window.innerWidth <= 768); } catch {} };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [posts, setPosts] = useState<Post[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<Post[]>([]);
  const [trendingItems, setTrendingItems] = useState<TrendingItem[]>([]);
  const [storyGroups, setStoryGroups] = useState<UserStories[]>([]);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerStartIndex, setViewerStartIndex] = useState(0);
  const [liveStreams, setLiveStreams] = useState<LiveStream[]>([]);
  const [endedStreams, setEndedStreams] = useState<LiveStream[]>([]);
  const [followingUsers, setFollowingUsers] = useState<string[]>([]);
  const [followedPages, setFollowedPages] = useState<string[]>([]);
  const [blockedUsers, setBlockedUsers] = useState<string[]>([]);
  const [userVerificationCache, setUserVerificationCache] = useState<Record<string, boolean>>({});
  const [mentionCandidates, setMentionCandidates] = useState<Array<{ id: string; name: string; username?: string; photoURL?: string }>>([]);
  const [suggestions, setSuggestions] = useState<{ open: boolean; kind: 'mention' | 'hashtag' | null; query: string; start: number }>({ open: false, kind: null, query: '', start: -1 });
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostAudience, setNewPostAudience] = useState<'public' | 'friends'>('public');
  const [uploadingMedia] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  // Additional photos/videos after the first one (multi-media posts).
  const [extraFiles, setExtraFiles] = useState<File[]>([]);
  const [thumbFile, setThumbFile] = useState<File | null>(null);
  const [canModerate, setCanModerate] = useState(false);
  useEffect(() => {
    if (!auth.currentUser) return;
    getUserMe()
      .then((me) => {
        const role = (me.profile as { role?: string } | undefined)?.role;
        setCanModerate(role === 'admin' || role === 'super_admin');
      })
      .catch(() => {});
  }, []);
  const [postBgMusic, setPostBgMusic] = useState<PickerMusicTrack | null>(null);
  // Background music mute state and refs per post
  // Per-post mute state (overrides), seeded by global mute preference
  const [bgMuted, setBgMuted] = useState<Record<string, boolean>>({});
  const globalMuteRef = useRef<boolean>(getGlobalMuted());
  const bgAudioRefs = useRef<Record<string, HTMLAudioElement | null>>({});
  // Container refs for intersection-based playback control
  const postContainerRefs = useRef<Record<string, HTMLElement | null>>({});

  const pauseAllExcept = (keepId?: string) => {
    Object.entries(bgAudioRefs.current).forEach(([k, a]) => {
      if (a && k !== keepId) {
        try { a.pause(); } catch {}
      }
    });
  };
  const toggleBgMute = (id: string) => {
    setBgMuted((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      const el = bgAudioRefs.current[id];
      if (el) {
        el.muted = !!next[id];
        if (!el.muted) {
          // pause other audios then play this one
          pauseAllExcept(id);
          try { el.play?.(); } catch {}
        }
      }
      return next;
    });
  };

  // Listen for global mute changes and apply to all audio elements (without overwriting per-post overrides if user unmuted a specific one while global is muted)
  useEffect(() => {
    const off = onGlobalMuteChange((muted) => {
      globalMuteRef.current = muted;
      Object.entries(bgAudioRefs.current).forEach(([id, el]) => {
        if (!el) return;
        // If global muted, force mute; if global unmuted and post not locally muted, attempt play
        if (muted) {
          el.muted = true;
          try { el.pause(); } catch {}
        } else if (!bgMuted[id]) {
          el.muted = false;
          pauseAllExcept(id);
          try { el.play(); } catch {}
        }
      });
    });
    return off;
  }, [bgMuted]);

  const pendingVerificationIds = useMemo(() => {
    const ids = new Set<string>();
    posts.forEach((post) => {
      if (post.userId && typeof post.userVerified === 'undefined') ids.add(post.userId);
      if (post.type === 'repost' && post.original?.userId && typeof post.original.userVerified === 'undefined') {
        ids.add(post.original.userId);
      }
    });
    trendingItems.forEach((item) => {
      if (item.userId && typeof item.userVerified === 'undefined') ids.add(item.userId);
    });
    return Array.from(ids).filter((id) => typeof userVerificationCache[id] === 'undefined');
  }, [posts, trendingItems, userVerificationCache]);

  useEffect(() => {
    if (!pendingVerificationIds.length) return;
    let cancelled = false;
    (async () => {
      const updates: Record<string, boolean> = {};
      await Promise.all(pendingVerificationIds.map(async (uid) => {
        try {
          const res = await getUserById(uid);
          updates[uid] = !!(res.profile as any)?.verified;
        } catch {
          updates[uid] = false;
        }
      }));
      if (!cancelled && Object.keys(updates).length) {
        setUserVerificationCache((prev) => ({ ...prev, ...updates }));
      }
    })();
    return () => { cancelled = true; };
  }, [pendingVerificationIds]);

  const resolveUserVerification = useCallback((userId: string, explicit?: boolean) => {
    if (typeof explicit === 'boolean') return explicit;
    const cached = userVerificationCache[userId];
    return typeof cached === 'boolean' ? cached : false;
  }, [userVerificationCache]);

  // Auto pause/resume background music based on viewport visibility
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const el = entry.target as HTMLElement;
        const id = el.dataset.postid;
        if (!id) return;
        const audio = bgAudioRefs.current[id];
        if (!audio) return;
        if (entry.isIntersecting) {
          // Pause all others, then resume this one only if not muted
          pauseAllExcept(id);
          if (!bgMuted[id] && !globalMuteRef.current) {
            try { audio.play(); } catch {}
          }
        } else {
          try { audio.pause(); } catch {}
        }
      });
    }, { threshold: 0.5 });

    // Observe current post containers
    Object.entries(postContainerRefs.current).forEach(([id, el]) => {
      if (el) observer.observe(el);
    });

    const refsSnapshot = { ...bgAudioRefs.current };

    // Cleanup: pause all audios on unmount
    return () => {
      try { observer.disconnect(); } catch {}
      Object.values(refsSnapshot).forEach((a) => { if (a) try { a.pause(); } catch {} });
    };
  }, [filteredPosts, bgMuted]);
  const [showPickPostMusic, setShowPickPostMusic] = useState(false);
  const mediaInputRef = React.useRef<HTMLInputElement | null>(null);
  // removed unused mediaSelectVersion ref
  const [posting, setPosting] = useState(false);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [editText, setEditText] = useState('');
  const [expandedComments, setExpandedComments] = useState<Record<string, boolean>>({});
  const [reportingPost, setReportingPost] = useState<{ id: string; userId: string } | null>(null);
  // Video editor for posts
  const [showEditor, setShowEditor] = useState(false);
  const MAX_POST_SECONDS = 60 * 60;
  const MAX_POST_MEDIA = 10;
  // Repost modal state
  const [showRepostModal, setShowRepostModal] = useState(false);
  const [repostTarget, setRepostTarget] = useState<Post | null>(null);
  const [quoteText, setQuoteText] = useState('');
  const [postingRepost, setPostingRepost] = useState(false);
  // Saved posts state (for immediate UI feedback and persistence)
  const [savedPostIds, setSavedPostIds] = useState<Record<string, boolean>>({});

  const postActionsRowStyle = useMemo(() => ({
    ...styles.postActionsRow,
    ...(isMobile ? styles.postActionsRowMobile : {}),
  }), [isMobile]);

  const actionButtonStyle = useMemo(() => ({
    ...styles.actionButton,
    ...(isMobile ? styles.actionButtonMobile : {}),
  }), [isMobile]);

  // Mention candidates are loaded alongside following list via the VPS API.

  // Repost helpers
  const openRepostModal = (p: Post) => {
    setRepostTarget(p);
    setQuoteText('');
    setShowRepostModal(true);
  };

  const submitRepost = async () => {
    if (!repostTarget || postingRepost) return;
    setPostingRepost(true);
    try {
      await handleRepost(repostTarget, quoteText);
      setShowRepostModal(false);
      setRepostTarget(null);
      setQuoteText('');
    } finally {
      setPostingRepost(false);
    }
  };

  // Load saved posts for current user (polling)
  useEffect(() => {
    if (!auth.currentUser) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await listSaved({ contentType: 'post', limit: 500 });
        if (cancelled) return;
        const map: Record<string, boolean> = {};
        (res.saved || []).forEach((item: any) => {
          if (item?.contentId) map[String(item.contentId)] = true;
        });
        setSavedPostIds(map);
      } catch (e) {
        console.warn('Failed to load saved posts', e);
      }
    };
    load();
    const t = window.setInterval(load, 30000);
    return () => { cancelled = true; window.clearInterval(t); };
  }, []);

  // Track a post view when its card mounts in the DOM
  const PostViewTracker: React.FC<{ id: string; category?: string }> = ({ id, category }) => {
    useEffect(() => {
      trackContentView(id, 'posts', category);
    }, [id, category]);
    return null;
  };

  // Load current user's following list and default audience
  useEffect(() => {
    const loadFollowingList = async () => {
      if (!auth.currentUser) return;
      const uid = auth.currentUser.uid;
      try {
        const [settingsRes, followingRes, pagesRes] = await Promise.all([
          getSettings(),
          listFollowing(uid, { limit: 500 }),
          listFollowedPages(),
        ]);

        const verified = !!(settingsRes.userData as any)?.verified;
        setUserVerificationCache((prev) => (typeof prev[uid] === 'boolean' ? prev : { ...prev, [uid]: verified }));

        setBlockedUsers(Array.isArray(settingsRes.settings?.blockedUsers) ? settingsRes.settings.blockedUsers : []);
        const audience = settingsRes.settings?.defaultAudience;
        if (audience === 'friends' || audience === 'public') {
          setNewPostAudience(audience);
        }

        const followingUsersList = (followingRes.users || []).map((u: any) => String(u.id));
        setFollowingUsers(followingUsersList);
        setMentionCandidates((followingRes.users || []).map((u: any) => ({
          id: String(u.id),
          name: String(u.name || 'Anonymous'),
          username: u.username ? String(u.username) : '',
          photoURL: u.photoURL ? String(u.photoURL) : '',
        })));

        setFollowedPages((pagesRes.pages || []).map((p: any) => String(p.id)));
      } catch (e) {
        console.warn('Failed to load user context', e);
      }
    };

    loadFollowingList();
  }, []);

  // Load live streams from followed users
  useEffect(() => {
    const loadLiveStreams = async () => {
      if (followingUsers.length === 0) return;
      try {
        const res = await listLiveStreams();
        const live: LiveStream[] = [];
        const ended: LiveStream[] = [];
        (res.streams || []).forEach((data: any) => {
          if (!data) return;
          if (data.userId && !followingUsers.includes(String(data.userId))) return;
          const stream: LiveStream = {
            id: String(data.id),
            userId: String(data.userId),
            userName: String(data.userName || ''),
            userPhoto: String(data.userPhoto || ''),
            title: String(data.title || ''),
            thumbnailURL: String(data.thumbnailURL || ''),
            viewers: data.viewers || 0,
            isLive: data.isLive,
            recordedURL: data.recordedURL || data.recordedUrl || '',
          };
          if (data.isLive) {
            live.push(stream);
          } else {
            ended.push(stream);
          }
        });
        setLiveStreams(live);
        setEndedStreams(ended);
      } catch (error) {
        console.error('Error loading live streams:', error);
      }
    };
    loadLiveStreams();
    // Refresh every 30 seconds
    const interval = setInterval(loadLiveStreams, 30000);
    return () => clearInterval(interval);
  }, [followingUsers]);

  // Load all posts
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await listPosts({ limit: 50, mode: 'feed' });
        if (cancelled) return;
        setPosts(res.posts as any);
      } catch (e) {
        console.error('Failed to load posts', e);
      }
    };
    load();
    const t = window.setInterval(load, 15000);
    return () => { cancelled = true; window.clearInterval(t); };
  }, []);

  // Load trending content (pulse + testimonies)
  useEffect(() => {
    let latestPulse: TrendingItem[] = [];
    let latestTestimony: TrendingItem[] = [];

    const pushCombined = () => {
      const combined = [...latestPulse, ...latestTestimony]
        .filter((i) => !!i.videoURL) // only show videos
        .sort((a, b) => b.score - a.score)
        .slice(0, 3); // cap to 3 items
      setTrendingItems(combined);
    };

    let cancelled = false;
    const load = async () => {
      try {
        const fetchMode = (mode: 'trending' | 'forYou') => Promise.all([
          listPulse({ limit: 10, mode }),
          listTestimonies({ limit: 10, mode }),
        ]);
        const hasVideo = (r: any[]) => (r[0].pulses || []).some((p: any) => p.videoURL) || (r[1].testimonies || []).some((t: any) => t.videoURL);
        let res: any[] = await fetchMode('trending');
        // Early on few items may qualify as trending; fall back to the ranked For You feed.
        if (!hasVideo(res)) res = await fetchMode('forYou');
        const [pulseRes, testRes] = res;
        if (cancelled) return;

        // Server applies the ranking rules; keep its order.
        latestPulse = (pulseRes.pulses || []).map((data: any, idx: number) => {
          const views = Number(data.views || 0);
          const score = 1000 - idx * 10;
          return {
            id: String(data.id),
            type: 'pulse' as const,
            userId: String(data.userId),
            userName: String(data.userName || ''),
            userPhoto: String(data.userPhoto || ''),
            title: data.caption || 'Pulse Video',
            caption: data.caption || '',
            videoURL: data.videoURL,
            thumbnailURL: data.thumbnailURL || null,
            imageURL: data.imageURL,
            likes: data.likes || [],
            views,
            score,
          } as TrendingItem;
        });

        latestTestimony = (testRes.testimonies || []).map((data: any, idx: number) => {
          const score = 995 - idx * 10;
          return {
            id: String(data.id),
            type: 'testimony' as const,
            userId: String(data.userId),
            userName: String(data.userName || ''),
            userPhoto: String(data.userPhoto || ''),
            title: data.title || 'Testimony',
            caption: data.caption || data.content || '',
            videoURL: data.videoURL,
            thumbnailURL: data.thumbnailURL || null,
            imageURL: data.imageURL,
            likes: data.likes || [],
            views: Number(data.views || 0),
            score,
          } as TrendingItem;
        });

        pushCombined();
      } catch (e) {
        console.warn('Failed to load trending content', e);
      }
    };

    load();
    const t = window.setInterval(load, 30000);
    return () => { cancelled = true; window.clearInterval(t); };

  }, []);

  // Filter posts based on following list and moderation status
  useEffect(() => {
    if (!auth.currentUser) return;

    // Always filter out blocked users
    let filtered = posts.filter((post) => !blockedUsers.includes(post.userId));

    // Moderation filter: show approved to everyone; show own pending
    filtered = filtered.filter((post) => {
      const status = (post as any).moderation?.status || 'approved';
      return status === 'approved' || post.userId === auth.currentUser!.uid;
    });

    // Audience filter: public visible to all; friends visible to owner and followers
    filtered = filtered.filter((post) => {
      const audience = (post as any).audience || 'public';
      if (audience === 'public') return true;
      // friends audience
      if (post.userId === auth.currentUser!.uid) return true;
      return followingUsers.includes(post.userId);
    });

    if (followingUsers.length === 0 && followedPages.length === 0) {
      // Show all posts if not following anyone yet
      setFilteredPosts(filtered);
    } else {
      // Show only posts from followed users + own posts
      filtered = filtered.filter((post) => {
        if (post.userId === auth.currentUser!.uid) return true;
        if (followingUsers.includes(post.userId)) return true;
        if (post.ownerType === 'page' && post.pageId && followedPages.includes(post.pageId)) return true;
        return false;
      });
      setFilteredPosts(filtered);
    }
  }, [posts, followingUsers, followedPages, blockedUsers]);

  // Load non-expired stories and group by user; prioritize followed-first
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await listStories({ limit: 200 });
        if (cancelled) return;
        const all: StoryDoc[] = (res.stories || []).map((s: any) => ({
          id: String(s.id),
          ...(s as any),
          createdAt: s.createdAt ? new Date(String(s.createdAt)) : undefined,
          expiresAt: s.expiresAt ? new Date(String(s.expiresAt)) : undefined,
        }));
      // Group by user
      const byUser = new Map<string, UserStories>();
      for (const s of all) {
        if (blockedUsers.includes(s.userId)) continue;
        if (!byUser.has(s.userId)) {

          byUser.set(s.userId, { userId: s.userId, userName: s.userName, userPhoto: s.userPhoto, userVerified: resolveUserVerification(s.userId, (s as any).userVerified), items: [] });
        }
        byUser.get(s.userId)!.items.push(s as StoryFull);
      }
  let groups = Array.from(byUser.values());
      // Sort each user's items by createdAt ascending (oldest to newest)
      groups.forEach(g => g.items.sort((a: any, b: any) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return ta - tb;
      }));
      // Filter to "you + followed" if available; fallback to all
      const uid = auth.currentUser?.uid || '';
      const followedSet = new Set([uid, ...followingUsers]);
      const preferred = groups.filter(g => followedSet.has(g.userId));
      groups = preferred.length > 0 ? preferred : groups;
      // Prioritize: you first, then followed, then others
      groups.sort((a, b) => {
        const score = (u: UserStories) => (
          (u.userId === uid ? 2 : 0) + (followingUsers.includes(u.userId) ? 1 : 0)
        );
        return score(b) - score(a);
      });
      setStoryGroups(groups);
      } catch (e) {
        console.warn('Failed to load stories', e);
      }
    };
    load();
    const t = window.setInterval(load, 30000);
    return () => { cancelled = true; window.clearInterval(t); };
  }, [followingUsers, blockedUsers, resolveUserVerification]);

  const openStoriesViewer = (idx: number) => {
    setViewerStartIndex(idx);
    setViewerOpen(true);
  };

  const handleCreatePost = async () => {
    if (!newPostContent.trim() && !mediaFile) return;
    if (!auth.currentUser) return;

    console.log('Creating post...', { hasContent: !!newPostContent.trim(), hasMedia: !!mediaFile });

    // Content validation - only validate if there's text content
    if (newPostContent.trim()) {
      const validation = validateContent(newPostContent);
      if (!validation.isValid) {
        alert(validation.message);
        return;
      }
    }

    setPosting(true);

    try {
      let imageURL = '';
      let videoURL = '';
      let mediaPath = '';
      let moderationTokens: Record<string, unknown> | null = null;
      const media: { url: string; type: 'image' | 'video' }[] = [];
      const hashtags = extractHashtags(newPostContent);
      const mentions = extractMentions(newPostContent);

      if (mediaFile) {
        setIsUploading(true);
        setUploadProgress(0);
        const files = [mediaFile, ...extraFiles];
        const tokens: Record<string, unknown> = {};
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const field = isVideoFile(file) || file.type.startsWith('video') ? 'videoURL' : 'imageURL';
          const upload = await uploadModeratedMedia({
            folder: field === 'videoURL' ? 'postVideos' : 'postImages',
            field,
            file,
            metadata: {
              caption: newPostContent,
              hashtags: hashtags.join(','),
              mentions: mentions.join(','),
            },
            onProgress: (pct) => setUploadProgress(((i + pct / 100) / files.length) * 100),
          });
          tokens[i === 0 ? upload.field : `media_${i}`] = upload.proof;
          media.push({ url: upload.objectKey, type: field === 'videoURL' ? 'video' : 'image' });
          if (i === 0) {
            mediaPath = upload.storagePath;
            if (field === 'imageURL') imageURL = upload.objectKey;
            else videoURL = upload.objectKey;
          }
        }
        moderationTokens = tokens;
      }
      const thumb = videoURL && thumbFile ? await uploadThumbnail(thumbFile) : null;
      if (thumb) moderationTokens = { ...(moderationTokens || {}), thumbnailURL: thumb.proof };

      const moderationStatus: 'approved' | 'pending' = 'approved';
      const moderationReason: string | null = null;

      const filteredMentions = mentions.filter((m) => m !== 'followers');

      // read username for the current user to embed on the post (enables username-first profile URLs)
      let currentUsername: string | null = null;
      try {
        const me = await getUserMe();
        currentUsername = (me.profile?.username || null) as any;
        // verified flag not used in post payload
      } catch {}

      let createdPostId: string | null = null;
      const created = await createPost({
        userName: auth.currentUser.displayName || 'Anonymous',
        username: currentUsername || null,
        userPhoto: (await getMyPhoto()),
        content: newPostContent,
        imageURL,
        videoURL,
        thumbnailURL: thumb?.thumbnailURL || null,
        media: media.length ? media : null,
        bgMusic: postBgMusic ? { id: postBgMusic.id, title: postBgMusic.title, artistId: postBgMusic.artistId, artistName: postBgMusic.artistName, audioURL: (postBgMusic as any).audioURL } : null,
        mediaPath,
        moderationTokens,
        moderation: {
          status: mediaFile ? moderationStatus : 'approved',
          reason: mediaFile ? moderationReason : null,
          scores: {},
        },
        audience: newPostAudience,
        hashtags,
        mentions,
      });
      createdPostId = created.id;
      setPosts((prev) => [created.post as any, ...prev]);
      notifyIfHeldForReview(created.post);

      // Notify mentioned users (best-effort)
      try {
        for (const uname of filteredMentions) {
          const res = await getUserByUsername(uname);
          const mentionedUserId = (res.profile as any)?.uid;
          if (mentionedUserId && mentionedUserId !== auth.currentUser!.uid) {
            await createNotification(
              mentionedUserId,
              'mention',
              auth.currentUser!.uid,
              auth.currentUser!.displayName || 'Someone',
              (await getMyPhoto()),
              `${auth.currentUser!.displayName || 'Someone'} mentioned you in a post`,
              createdPostId || 'unknown',
              'post'
            );
          }
        }
      } catch (e) {
        console.warn('Failed sending mention notifications', e);
      }

      // @followers fanout is now handled in a Cloud Function for scale and rate limits.

  console.log('Post created successfully');
  setNewPostContent('');
  setNewPostAudience('public');
      setMediaFile(null);
      setExtraFiles([]);
      setThumbFile(null);
      setPostBgMusic(null);
      setUploadProgress(0);
    } catch (error) {
      console.error('Post creation failed:', error);
      const friendly = describeModerationError(error);
      alert(friendly || 'Failed to create post');
    } finally {
      setIsUploading(false);
      setPosting(false);
    }
  };

  const handleLikePost = async (postId: string) => {
    if (!auth.currentUser) return;
    const target = posts.find((p) => p.id === postId);
    if (!target) return;
    const isLiked = !!target.likedByMe;
    try {
      if (isLiked) {
        await unlikePostApi(postId);
      } else {
        await likePostApi(postId);
      }
      setPosts((prev) => prev.map((p) => {
        if (p.id !== postId) return p;
        const nextCount = Math.max(0, (p.likeCount || 0) + (isLiked ? -1 : 1));
        return { ...p, likedByMe: !isLiked, likeCount: nextCount } as Post;
      }));
      trackInteraction(postId, 'like');
    } catch (error) {
      console.error('Like failed:', error);
    }
  };

  const handleMediaSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []).filter((f) => {
      if (f.size <= MAX_UPLOAD_BYTES) return true;
      alert(`"${f.name}" is larger than 500 MB. Please choose a smaller or compressed file.`);
      return false;
    });
    // Reset so the same file can be picked again after removing it.
    e.target.value = '';
    if (!picked.length) return;
    const room = MAX_POST_MEDIA - (mediaFile ? 1 : 0) - extraFiles.length;
    if (room <= 0) {
      alert(`You can add up to ${MAX_POST_MEDIA} photos or videos to one post.`);
      return;
    }
    if (picked.length > room) {
      alert(`You can add up to ${MAX_POST_MEDIA} photos or videos to one post, so only the first ${room} were added.`);
    }
    const queue = picked.slice(0, room);
    const primary = mediaFile ? null : queue.shift() || null;
    const extrasPromise = Promise.all(queue.map(prepareExtraMedia));
    if (primary) await selectPrimaryMedia(primary);
    const extras = (await extrasPromise).filter((f): f is File => !!f);
    if (extras.length) setExtraFiles((prev) => [...prev, ...extras].slice(0, MAX_POST_MEDIA - 1));
  };

  // Extra items skip the editor: images are compressed, over-length videos are rejected.
  const prepareExtraMedia = async (file: File): Promise<File | null> => {
    if (isVideoFile(file)) {
      try {
        const duration = await getVideoDuration(file);
        if (duration > MAX_POST_SECONDS + 0.5) {
          alert(`"${file.name}" is longer than 1 hour. Videos in posts can be up to 1 hour long.`);
          return null;
        }
      } catch {}
      return file;
    }
    try {
      return await compressImage(file, { maxWidth: Infinity, maxHeight: Infinity, quality: 0.86, mimeType: 'image/webp' });
    } catch {
      return file;
    }
  };

  const selectPrimaryMedia = async (file: File) => {
    console.log('File selected:', file.name, file.type, file.size);
    // Video path unchanged
    if (isVideoFile(file)) {
      try {
        const duration = await getVideoDuration(file);
        if (duration > MAX_POST_SECONDS + 0.5) {
          alert(`"${file.name}" is longer than 1 hour. Videos in posts can be up to 1 hour long.`);
          return;
        }
      } catch (err) {
        console.warn('Failed to read video duration', err);
      }
      setMediaFile(file);
      setShowEditor(false);
      return;
    }
    // Image compression (in background) then set
    setMediaFile(file); // optimistic immediate preview
    try {
      const compressed = await compressImage(file, { maxWidth: Infinity, maxHeight: Infinity, quality: 0.86, mimeType: 'image/webp' });
      if (compressed !== file) {
        console.log('Compressed image from', file.size, 'to', compressed.size);
        setMediaFile(compressed);
      }
    } catch (err) {
      console.warn('Compression failed, using original', err);
    }
  };

  const handleRemoveSelectedMedia = () => {
    setMediaFile(null);
    setExtraFiles([]);
    setThumbFile(null);
    if (mediaInputRef.current) {
      mediaInputRef.current.value = '';
    }
  };

  const removeSelectedAt = (index: number) => {
    if (index === 0) {
      if (!extraFiles.length) return handleRemoveSelectedMedia();
      setMediaFile(extraFiles[0]);
      setExtraFiles(extraFiles.slice(1));
      setThumbFile(null);
      return;
    }
    setExtraFiles((prev) => prev.filter((_, i) => i !== index - 1));
  };

  const handleShare = async (postId: string) => {
    const shareUrl = `${window.location.origin}/post/${postId}`;
    const text = 'Check out this post on Belivah';
    try {
      if ((navigator as any).share) {
        await (navigator as any).share({ title: 'Belivah', text, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        alert('Link copied to clipboard');
      }
      // Track share interaction
      trackInteraction(postId, 'share');
    } catch (e) {
      console.warn('Share failed', e);
    }
  };

  const handleDeletePost = async (p: Post) => {
    if (!auth.currentUser) return;
    const isOwner = p.userId === auth.currentUser.uid;
    if (!isOwner && !canModerate) return;
    const ok = await appConfirm(isOwner ? 'Delete this post? This cannot be undone.' : 'Admin: remove this post? This cannot be undone.');
    if (!ok) return;
    try {
      // Best-effort: delete media from storage if present
      try {
        const mediaPath = p.mediaPath as string | undefined;
        if (mediaPath && String(mediaPath).startsWith('media/')) {
          await deleteMediaObject(String(mediaPath)).catch(() => {});
        }
        for (const m of p.media || []) {
          if (m.url !== mediaPath && m.url.startsWith('media/')) await deleteMediaObject(m.url).catch(() => {});
        }
      } catch {}
      await deletePostApi(p.id);
      setPosts((prev) => prev.filter((x) => x.id !== p.id));
    } catch (e) {
      console.error('Delete failed', e);
      alert('Failed to delete post');
    }
  };

  const openEditPost = (p: Post) => {
    if (!auth.currentUser || p.userId !== auth.currentUser.uid) return;
    setEditingPost(p);
    setEditText(p.content || '');
  };

  const submitEditPost = async () => {
    if (!editingPost || !auth.currentUser) return;
    try {
      await updatePostApi(editingPost.id, { content: editText });
      setPosts((prev) => prev.map((p) => p.id === editingPost.id ? ({ ...p, content: editText } as Post) : p));
      setEditingPost(null);
      setEditText('');
    } catch (e) {
      console.error('Edit failed', e);
      alert('Failed to update post');
    }
  };

  const toggleBookmark = async (postId: string) => {
    if (!auth.currentUser) return;
    try {
      // Optimistic UI update
      const currentlySaved = !!savedPostIds[postId];
      setSavedPostIds((prev) => {
        const next = { ...prev };
        if (currentlySaved) delete next[postId];
        else next[postId] = true;
        return next;
      });

      if (currentlySaved) {
        await unsaveItem('post', postId);
      } else {
        await saveItem({ contentType: 'post', contentId: postId });
      }
    } catch (e) {
      console.error('Bookmark toggle failed', e);
      // Revert UI on failure
      setSavedPostIds((prev) => {
        const next = { ...prev };
        if (next[postId]) delete next[postId];
        else next[postId] = true;
        return next;
      });
    }
  };

  const handleRepost = async (p: Post, quote?: string) => {
    if (!auth.currentUser) return;
    try {
      // Build a sanitized snapshot of the original post without undefined fields
      const buildOriginal = (post: Post) => {
        const base: any = {
          id: post.id,
          userId: post.userId,
          userName: post.userName,
          userPhoto: post.userPhoto || '',
          content: post.content || '',
          createdAt: post.createdAt,
        };
        base.userVerified = resolveUserVerification(post.userId, (post as any).userVerified);
        if (post.imageURL) base.imageURL = post.imageURL;
        if (post.videoURL) base.videoURL = post.videoURL;
        return base;
      };

      const orig = p.type === 'repost' && p.original
        ? buildOriginal({
            // Re-wrap the minimal fields from p.original to ensure no undefineds
            id: p.original.id,
            userId: p.original.userId,
            userName: p.original.userName,
            userPhoto: p.original.userPhoto,
            userVerified: p.original.userVerified,
            content: p.original.content,
            imageURL: p.original.imageURL,
            videoURL: p.original.videoURL,
            createdAt: p.original.createdAt,
            likeCount: 0,
            likedByMe: false,
            comments: 0,
          } as Post)
        : buildOriginal(p);

      let currentUsername2: string | null = null;
      try {
        const me = await getUserMe();
        currentUsername2 = ((me.profile as any)?.username || null) as any;
      } catch {}
      const created = await createPost({
        userName: auth.currentUser.displayName || 'Anonymous',
        username: currentUsername2 || null,
        userPhoto: (await getMyPhoto()),
        content: ((quote || '').trim() || ' '),
        type: 'repost',
        repostOf: p.type === 'repost' ? (p.original?.id || p.repostOf || p.id) : p.id,
        original: orig,
        moderation: { status: 'approved', reason: null, scores: {} },
      });

      setPosts((prev) => [created.post as any, ...prev]);
      notifyIfHeldForReview(created.post);

      alert('Reposted to your timeline');

      // Optional: notify original owner (skipped: supported types are like/comment/follow/mention)
    } catch (e) {
      console.error('Repost failed', e);
      alert('Failed to repost');
    }
  };


  const trendingStrip = trendingItems.length > 0 ? (
                <div style={styles.trendingSection}>
                  <h3 style={styles.trendingTitle}>🔥 Trending Now</h3>
                  <div style={styles.trendingGrid} className="hide-scrollbar">
                    {trendingItems.map((item) => {
                      const trendingVerified = resolveUserVerification(item.userId, item.userVerified);
                      return (
                        <div
                          key={`${item.type}-${item.id}`}
                          style={{ ...styles.trendingCard, flex: isMobile ? '0 0 42%' : '0 0 168px' }}
                          className="trending-card"
                          onClick={() =>
                            navigate(
                              item.type === 'pulse'
                                ? `/pulse?videoId=${encodeURIComponent(item.id)}`
                                : `/testimony?id=${encodeURIComponent(item.id)}`
                            )
                          }
                        >
                          {(item.videoURL || item.imageURL) && (
                            <div
                              style={{
                                ...styles.trendingMediaContainer,
                                aspectRatio: '9 / 16',
                              }}
                            >
                              {item.videoURL ? (
                                <AutoplayPreview
                                  src={item.videoURL}
                                  poster={item.thumbnailURL}
                                  style={{
                                    ...styles.trendingMedia,
                                    height: '100%',
                                    objectFit: 'cover' as const,
                                  }}
                                />
                              ) : (
                                <ImageWithFallback
                                  src={item.imageURL}
                                  alt=""
                                  style={{
                                    ...styles.trendingMedia,
                                    height: '100%',
                                    objectFit: 'cover' as const,
                                  }}
                                />
                              )}
                              <div style={styles.trendingBadge}>
                                {item.type === 'pulse' ? '🎬 Pulse' : '🙏 Testimony'}
                              </div>
                            </div>
                          )}
                          <div style={styles.trendingContent}>
                            <h4 style={styles.trendingItemTitle}>{item.title}</h4>
                            {item.caption && (
                              <p style={styles.trendingCaption}>{item.caption}...</p>
                            )}
                            <div style={styles.trendingMeta}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                by <UserLink userId={item.userId}>{item.userName}</UserLink>
                                {trendingVerified && <VerifiedBadge size={12} />}
                              </span>
                              <span>❤️ {item.likes.length}</span>
                              {item.views !== undefined && <span>👁️ {item.views}</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
  ) : null;

  return (
    <div style={styles.container}>
      <TopNavigation />
      {!isMobile && <LeftNavSidebar />}
      {!isMobile && <RightWidgetsSidebar />}
      <div style={{
        ...styles.contentArea,
        ...(isMobile ? styles.contentAreaMobile : {}),
      }} className="content-area">
        <main style={{ ...styles.main, ...(isMobile ? styles.mainMobile : {}) }}>
        {/* Stories rail */}
        <div style={styles.storiesRail}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={styles.storyItem} className="story-item" onClick={() => navigate('/stories')}>
              <div style={{ position: 'relative' }}>
                <div style={styles.storyRing}>
                  <div style={styles.storyInner}>
                    <ImageWithFallback
                      src={myPhoto || undefined}
                      alt=""
                      style={styles.storyAvatar}
                      className="story-avatar"
                    />
                  </div>
                </div>
                <div style={styles.addStory}>+</div>
              </div>
              <div style={styles.storyName}>Your Story</div>
            </div>
          </div>
          <StoriesRail groups={storyGroups} onOpen={openStoriesViewer} />
        </div>

        {/* Mobile search under stories removed per request; search is available in bottom nav */}

        {/* Live Streams from Following */}
        {(liveStreams.length > 0 || endedStreams.length > 0) && (
          <div style={styles.liveStreamsSection}>
            <div style={styles.liveStreamsHeader}>
              <h3 style={styles.liveStreamsTitle}>🔴 Live Now</h3>
              <button 
                onClick={() => navigate('/live')} 
                style={styles.seeAllButton}
              >
                See All
              </button>
            </div>
            <div style={styles.liveStreamsScroll}>
              {liveStreams.map((stream) => (
                <div 
                  key={stream.id} 
                  style={styles.liveStreamCard}
                  onClick={() => navigate(`/live/${stream.id}`)}
                >
                  <div style={styles.liveStreamThumbnail}>
                    <ImageWithFallback
                      src={stream.thumbnailURL}
                      alt={stream.title}
                      style={styles.liveStreamImage}
                    />
                    <div style={styles.liveBadge}>
                      <span style={styles.liveDot}>●</span>
                      LIVE
                    </div>
                    <div style={styles.liveViewers}>
                      👁️ {stream.viewers}
                    </div>
                  </div>
                  <div style={styles.liveStreamInfo}>
                    <ImageWithFallback
                      src={stream.userPhoto}
                      alt={stream.userName}
                      style={styles.liveStreamAvatar}
                    />
                    <div style={styles.liveStreamDetails}>
                      <div style={styles.liveStreamTitle}>{stream.title}</div>
                      <div style={styles.liveStreamUser}><UserLink userId={stream.userId}>{stream.userName}</UserLink></div>
                    </div>
                  </div>
                </div>
              ))}
              {/* Ended Streams */}
              {endedStreams.length > 0 && (
                <>
                  <div style={{ margin: '24px 0 8px 0', fontWeight: 600, fontSize: 16 }}>Recent Broadcasts</div>
                  {endedStreams.map((stream) => (
                    <div 
                      key={stream.id} 
                      style={styles.liveStreamCard}
                      onClick={() => navigate(`/live/${stream.id}`)}
                    >
                      <div style={styles.liveStreamThumbnail}>
                        <ImageWithFallback
                          src={stream.thumbnailURL}
                          alt={stream.title}
                          style={styles.liveStreamImage}
                        />
                        <div style={{ ...styles.liveBadge, backgroundColor: '#aaa' }}>
                          <span style={{ ...styles.liveDot, color: '#aaa' }}>●</span>
                          Was Live
                        </div>
                        <div style={styles.liveViewers}>
                          👁️ {stream.viewers}
                        </div>
                      </div>
                      <div style={styles.liveStreamInfo}>
                        <ImageWithFallback
                          src={stream.userPhoto}
                          alt={stream.userName}
                          style={styles.liveStreamAvatar}
                        />
                        <div style={styles.liveStreamDetails}>
                          <div style={styles.liveStreamTitle}>{stream.title}</div>
                          <div style={styles.liveStreamUser}><UserLink userId={stream.userId}>{stream.userName}</UserLink></div>
                        </div>
                        {stream.recordedURL && (
                          <button
                            style={{marginTop:8,padding:'8px 16px',borderRadius:6,background:'#17A2A9',color:'#fff',border:'none',cursor:'pointer'}}
                            onClick={(e)=>{ e.stopPropagation(); navigate(`/live/${stream.id}`); }}
                          >Rewatch</button>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        )}

        {/* Music picker modal for image posts */}
        {showPickPostMusic && (
          <MusicLibraryPicker
            open={showPickPostMusic}
            onClose={() => setShowPickPostMusic(false)}
            onSelect={(t) => { setPostBgMusic(t); setShowPickPostMusic(false); }}
          />
        )}

        {/* Quick links: original grid layout */}
        <div style={{ ...styles.quickLinks, ...(isMobile ? styles.quickLinksMobile : {}) }}>
          <div
            style={{ ...styles.linkCard, ...(isMobile ? styles.linkCardMobile : {}) }}
            className="link-card"
            onClick={() => navigate('/live')}
          >
            <div style={{ fontSize: 20 }}>🔴</div>
            <div>Go Live</div>
          </div>
          <div
            style={{ ...styles.linkCard, ...(isMobile ? styles.linkCardMobile : {}) }}
            className="link-card"
            onClick={() => navigate('/pulse')}
          >
            <div style={{ fontSize: 20 }}>🎬</div>
            <div>Share the Vibe</div>
          </div>
        </div>

        {/* Removed duplicate mobile search below quick links to avoid repetition */}

        <div style={styles.createPost} className="create-post">
          <h3 style={styles.createPostTitle}>Create Post</h3>
          <div style={{ ...styles.createPostRow, ...(isMobile ? { alignItems: 'stretch' } : {}) }}>
            <ImageWithFallback
              src={myPhoto || undefined}
              alt=""
              style={styles.createAvatar}
            />
            <textarea
              value={newPostContent}
              onChange={(e) => {
                const v = e.target.value;
                setNewPostContent(v);
                try {
                  const caret = e.target.selectionStart || v.length;
                  const head = v.slice(0, caret);
                  const m = head.match(/([#@][a-zA-Z0-9_]*)$/);
                  if (m) {
                    const token = m[1];
                    const kind = token.startsWith('@') ? 'mention' : 'hashtag';
                    const queryTxt = token.slice(1).toLowerCase();
                    setSuggestions({ open: queryTxt.length >= 1, kind, query: queryTxt, start: caret - token.length });
                  } else {
                    setSuggestions({ open: false, kind: null, query: '', start: -1 });
                  }
                } catch {
                  setSuggestions({ open: false, kind: null, query: '', start: -1 });
                }
              }}
              placeholder="What's on your mind?"
              style={styles.textarea}
              rows={3}
            />
            {suggestions.open && (
              <div style={styles.suggestBox}>
                {suggestions.kind === 'mention' ? (
                  <>
                    <div style={styles.suggestHeader}>Mention</div>
                    {[{ id: '__followers__', name: 'Followers', username: 'followers', photoURL: '' }, ...mentionCandidates]
                      .filter((u) => (u.username || '').toLowerCase().includes(suggestions.query) || (u.name || '').toLowerCase().includes(suggestions.query))
                      .slice(0, 6)
                      .map((u) => (
                        <div key={u.id} style={styles.suggestItem} onMouseDown={(ev) => {
                          ev.preventDefault();
                          const textarea = ev.currentTarget.closest('.create-post')?.querySelector('textarea') as HTMLTextAreaElement | null;
                          const caret = textarea?.selectionStart || newPostContent.length;
                          const before = newPostContent.slice(0, suggestions.start);
                          const after = newPostContent.slice(caret);
                          const isSpecial = u.id === '__followers__';
                          const uname = isSpecial ? 'followers' : (u.username || u.name.replace(/\\\\s+/g,'').toLowerCase());
                          const ins = `@${uname} `;
                          const next = before + ins + after;
                          setNewPostContent(next);
                          setSuggestions({ open: false, kind: null, query: '', start: -1 });
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{
                              width: 24, height: 24, borderRadius: '50%',
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              background: u.id === '__followers__' ? 'var(--color-primary-50)' : 'transparent',
                              color: 'var(--color-primary-700)'
                            }}>
                              {u.id === '__followers__' ? '👥' : <ImageWithFallback src={u.photoURL || undefined} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }} />}
                            </div>
                            <div>
                              <div style={{ fontWeight: 600 }}>{u.name}</div>
                              <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>@{u.username || u.id.slice(0,6)}</div>
                            </div>
                          </div>
                        </div>
                      ))}

                    {mentionCandidates.filter((u) => (u.username || '').toLowerCase().includes(suggestions.query) || (u.name || '').toLowerCase().includes(suggestions.query)).length === 0 && (
                      <div style={styles.suggestEmpty}>No matches</div>
                    )}
                  </>
                ) : (
                  <>
                    <div style={styles.suggestHeader}>Hashtags</div>
                    {Array.from(new Set(posts.flatMap((p) => (p as any).hashtags || [])))
                      .filter((t) => String(t).toLowerCase().includes(suggestions.query))
                      .slice(0, 6)
                      .map((tag) => (
                        <div key={String(tag)} style={styles.suggestItem} onMouseDown={(ev) => {
                          ev.preventDefault();
                          const textarea = ev.currentTarget.closest('.create-post')?.querySelector('textarea') as HTMLTextAreaElement | null;
                          const caret = textarea?.selectionStart || newPostContent.length;
                          const before = newPostContent.slice(0, suggestions.start);
                          const after = newPostContent.slice(caret);
                          const ins = `#${String(tag).toLowerCase()} `;
                          const next = before + ins + after;
                          setNewPostContent(next);
                          setSuggestions({ open: false, kind: null, query: '', start: -1 });
                        }}>
                          <span style={{ fontWeight: 700, color: 'var(--color-primary-700)' }}>#{String(tag).toLowerCase()}</span>
                        </div>
                      ))}

                  </>
                )}
              </div>
            )}
          </div>
          <div style={{ ...styles.createPostActions, ...(isMobile ? styles.createPostActionsMobile : {}) }} className="create-post-actions">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label htmlFor="audience-select" style={{ fontSize: 12, color: 'var(--text-muted)' }}>Audience</label>
              <select
                id="audience-select"
                value={newPostAudience}
                onChange={(e) => setNewPostAudience(e.target.value as 'public' | 'friends')}
                style={{
                  padding: '6px 8px',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  background: 'var(--card)',
                  color: 'var(--text)',
                  outline: 'none'
                }}
              >
                <option value="public">Public</option>
                <option value="friends">Friends</option>
              </select>
            </div>
            <label
              htmlFor="media-upload"
              style={styles.mediaLabel}
              className="media-label"
              title={mediaFile ? `Add more photos or videos (${1 + extraFiles.length}/${MAX_POST_MEDIA})` : '📷 Add Photo/Video'}
            >
              {mediaFile ? `➕ Add more (${1 + extraFiles.length}/${MAX_POST_MEDIA})` : '📷 Add Photo/Video'}
            </label>
            <input
              id="media-upload"
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleMediaSelect}
              style={styles.fileInput}
              // Allow changing while optimizing
              disabled={false}
              ref={mediaInputRef}
            />
            {mediaFile && mediaFile.type.startsWith('image/') && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {postBgMusic ? (
                  <>
                    <span style={{ fontWeight: 600 }}>🎵 {postBgMusic.title}</span>
                    <button type="button" style={styles.smallButtonDanger} onClick={() => setPostBgMusic(null)}>Clear</button>
                  </>
                ) : (
                  <button type="button" style={styles.smallButtonSecondary} onClick={() => setShowPickPostMusic(true)}>Add Music</button>
                )}
              </div>
            )}
            {mediaFile && !uploadingMedia && (
              <div style={styles.mediaActionsInline}>
                {/* Removed Change button per request; only showing Remove */}
                <button type="button" style={styles.smallButtonDanger} onClick={handleRemoveSelectedMedia}>
                  {extraFiles.length ? 'Remove all' : 'Remove'}
                </button>
              </div>
            )}
            <button
              onClick={handleCreatePost}
              disabled={posting || (!newPostContent.trim() && !mediaFile)}
              style={{ ...styles.postButton, ...(isMobile ? { padding: '10px 16px', fontSize: 14 } : {}) }}
              className="post-button"
            >
              {posting ? 'Posting...' : 'Post'}
            </button>
          </div>
          {mediaFile && !isUploading && (
            <div style={{ display: 'flex', gap: 10, overflowX: 'auto', padding: '10px 6px 4px' }}>
              {extraFiles.length === 0 ? (
                <SelectedMediaThumb key={`${mediaFile.name}-${mediaFile.size}`} file={mediaFile} large onRemove={handleRemoveSelectedMedia} />
              ) : [mediaFile, ...extraFiles].map((f, i) => (
                <SelectedMediaThumb key={`${i}-${f.name}-${f.size}`} file={f} label={`${i + 1}`} onRemove={() => removeSelectedAt(i)} />
              ))}
            </div>
          )}
          {mediaFile && isVideoFile(mediaFile) && extraFiles.length === 0 && !isUploading && (
            <button
              type="button"
              onClick={() => setShowEditor(true)}
              disabled={posting}
              style={{ margin: '6px 6px 0', padding: '8px 14px', borderRadius: 8, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#3730a3', fontWeight: 600, cursor: 'pointer' }}
            >
              ✂️ Edit video / add music (optional)
            </button>
          )}
          {mediaFile && isVideoFile(mediaFile) && !uploadingMedia && (
            <ThumbnailPicker file={thumbFile} onChange={setThumbFile} disabled={posting} videoFile={mediaFile} />
          )}
          {isUploading && (
            <div style={styles.progressWrap}>
              <div style={styles.progressHeader}>
                <span>Uploading media</span>
                <span style={styles.progressPct}>{Math.round(uploadProgress)}%</span>
              </div>
              <div style={styles.progressTrack}>
                <div style={{ ...styles.progressFill, width: `${Math.max(0, Math.min(100, uploadProgress))}%` }} />
              </div>
            </div>
          )}
        </div>


        <div style={styles.feed}>
          {filteredPosts.length === 0 ? (
            <div style={styles.emptyFeedContainer}>
              <p style={styles.emptyFeed}>
                {followingUsers.length === 0 
                  ? "Welcome! Follow some users to see their posts in your feed." 
                  : "No posts from followed users yet."}
              </p>
            </div>
          ) : (
            <>
              {/* First post */}
              {(() => {
                const post = filteredPosts[0];
                const idx = 0;
                const postVerified = resolveUserVerification(post.userId, post.userVerified);
                return (
                  <React.Fragment key={post.id}>
                  <div style={styles.postCard} className="post-card">
                    <PostViewTracker id={post.id} />
                    <div style={styles.postHeader}>
                      <ImageWithFallback 
                        src={(post.ownerType === 'page' ? post.pagePhoto : post.userPhoto) || undefined} 
                        alt="" 
                        style={styles.userAvatar}
                        className="user-avatar"
                        onClick={() => post.ownerType === 'page' && post.pageId ? navigate(`/page/${post.pageId}`) : navigate(getProfileUrl({ id: post.userId, username: (post as any).username }))}
                      />
                      <div>
                        <strong 
                          style={styles.userName}
                          className="user-name"
                          onClick={() => post.ownerType === 'page' && post.pageId ? navigate(`/page/${post.pageId}`) : navigate(getProfileUrl({ id: post.userId, username: (post as any).username }))}
                        >
                          {post.ownerType === 'page' ? (post.pageName || 'Page') : post.userName}
                          {postVerified && post.ownerType !== 'page' && <VerifiedBadge size={14} />}
                        </strong>
                        {(['pending','needs_review'].includes((post as any).moderation?.status)) && post.userId === (auth.currentUser?.uid || '') && (
                          <span style={styles.pendingBadge}>Pending review</span>
                        )}
                        <p style={styles.postTime}>
                          {post.type === 'repost' ? 'Reposted • ' : ''}
                          {post.createdAt?.toDate?.()?.toLocaleString?.() || (post.createdAt ? new Date(post.createdAt).toLocaleString() : 'Just now')}
                        </p>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <span style={styles.audienceBadge}>
                            {(((post as any).audience || 'public') === 'public') ? '🌍 Public' : '👥 Friends'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {post.type === 'repost' && post.original ? (
                      <div style={styles.repostWrapper}>
                        <div style={styles.repostHeader}>
                          Reposted from{' '}
                          <span
                            style={{ color: 'var(--color-primary-700)', cursor: 'pointer', fontWeight: 600 }}
                            onClick={() => navigate(getProfileUrl({ id: post.original!.userId, username: (post.original as any)?.username }))}
                          >
                            {post.original.userName}
                          </span>
                        </div>
                        {post.original.content && (
                          <p style={styles.postContent}>{renderTextWithLinks(post.original.content)}</p>
                        )}
                        {post.original.imageURL && (
                          <MediaBox src={post.original.imageURL} type="image" alt="Original image" />
                        )}
                        {post.original.videoURL && (
                          <MediaBox src={post.original.videoURL} type="video" controls contain={true} />
                        )}
                      </div>
                    ) : post.type && (post.type === 'live_repost' || post.type === 'recording_repost') ? (
                      <div style={styles.repostWrapper}>
                        <div style={styles.repostHeader}>
                          {post.type === 'live_repost' ? 'Reposted Live Stream' : 'Reposted Recording'}
                        </div>
                        <div
                          style={{
                            position: 'relative',
                            border: '1px solid var(--border)',
                            borderRadius: 12,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            background: '#000'
                          }}
                          onClick={() => post.originalStreamId && navigate(`/live/${post.originalStreamId}`)}
                        >
                          {post.streamThumbnail ? (
  <img
    src={post.streamThumbnail}
    alt={post.title || 'Live stream thumbnail'}
    style={{
      width: '100%',
      display: 'block',
      maxHeight: 320,
      objectFit: 'cover',
    }}
  />
) : (
  <div
    style={{
      width: '100%',
      paddingTop: '56.25%',
      background: 'rgba(255,255,255,0.08)',
    }}
  />
)}
                          <div style={{ position: 'absolute', top: 8, left: 8, background: post.type === 'live_repost' ? '#DC2626' : '#3b82f6', color: '#fff', fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 8 }}>
                            {post.type === 'live_repost' ? 'LIVE' : 'RECORDED'}
                          </div>
                          {post.title && (
                            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0))', color: '#fff', padding: '12px 14px', fontSize: 14, fontWeight: 600 }}>
                              {post.title}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <>
                        <p style={styles.postContent}>{renderTextWithLinks(post.content)}</p>
                        {(hasMultiMedia(post) || post.imageURL) && (
                          <div style={{ position: 'relative' }} ref={(el) => { postContainerRefs.current[post.id] = el; if (el) el.dataset.postid = post.id; }}>
                            {hasMultiMedia(post)
                              ? <PostMediaGallery media={post.media || []} poster={post.thumbnailURL} postId={post.id} />
                              : <MediaBox src={post.imageURL || ''} type="image" alt="Post image" />}
                            {(post as any).bgMusic?.audioURL && (
                              <div style={{ marginTop: 0 }}>
                                {/* Hidden auto-playing background audio for image posts */}
                                <SignedAudio
                                  autoPlay
                                  loop
                                  playsInline
                                  controls={false}
                                  preload="none"
                                  src={(post as any).bgMusic.audioURL}
                                  muted={!!bgMuted[post.id]}
                                  ref={(el) => { if (el) { bgAudioRefs.current[post.id] = el; el.muted = !!bgMuted[post.id]; } }}
                                  onPlay={() => {}}
                                  style={{ display: 'none' }}
                                />
                              </div>
                            )}
                             {(post as any).bgMusic?.audioURL && (
                              <button
                                aria-label={bgMuted[post.id] ? 'Unmute background music' : 'Mute background music'}
                                onClick={() => toggleBgMute(post.id)}
                                style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: 20, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 2 }}
                              >
                                {bgMuted[post.id] ? '🔇' : '🔊'}
                              </button>
                            )}
                          </div>
                        )}
                        {!hasMultiMedia(post) && post.videoURL && (
                          <MediaBox src={post.videoURL} poster={post.thumbnailURL} type="video" controls contain={true} watchContent={{ type: 'post', id: post.id }} />
                        )}
                      </>
                    )}

                    <div style={{ ...styles.postActions }}>
                      <div style={postActionsRowStyle}>
                        <button
                          onClick={() => handleLikePost(post.id)}
                          style={actionButtonStyle}
                          className="action-button"
                        >
                          {post.likedByMe ? '❤️' : '🤍'} {post.likeCount || 0}
                        </button>
                        <button
                          onClick={() => { trackInteraction(post.id, 'comment'); setExpandedComments((m) => ({ ...m, [post.id]: !m[post.id] })); }}
                          style={actionButtonStyle}
                          className="action-button"
                        >
                          💬 {post.comments}
                        </button>
                        <button
                          onClick={() => handleShare(post.id)}
                          style={actionButtonStyle}
                          className="action-button"
                        >
                          🔗 Share
                        </button>
                        <button
                          onClick={() => toggleBookmark(post.id)}
                          style={actionButtonStyle}
                          className="action-button"
                        >
                          {savedPostIds[post.id] ? '✅ Saved' : '📌 Save'}
                        </button>
                        {auth.currentUser && post.userId === auth.currentUser.uid && post.type !== 'repost' && (
                          <>
                            <button
                              onClick={() => openEditPost(post)}
                              style={actionButtonStyle}
                              className="action-button"
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => handleDeletePost(post)}
                              style={actionButtonStyle}
                              className="action-button"
                            >
                              🗑️ Delete
                            </button>
                          </>
                        )}
                        {canModerate && auth.currentUser && post.userId !== auth.currentUser.uid && (
                          <button onClick={() => handleDeletePost(post)} style={{ ...actionButtonStyle, color: 'var(--color-error)' }} className="action-button">🛡️ Remove</button>
                        )}
                        {auth.currentUser && post.userId !== auth.currentUser.uid && post.type !== 'repost' && (
                          <>
                            <button
                              onClick={() => openRepostModal(post)}
                              style={actionButtonStyle}
                              className="action-button"
                            >
                              🔁 Repost
                            </button>
                            <button
                              onClick={() => setReportingPost({ id: post.id, userId: post.userId })}
                              style={{ ...actionButtonStyle, color: 'var(--color-error)' }}
                              className="action-button report-button"
                            >
                              🚩 Report
                            </button>
                          </>
                        )}
                      </div>
                      
                    </div>
                    {((idx + 1) % 5 === 0) && (
                      <div key={`ad-${post.id}`} style={{ marginTop: 12 }}>
                        <AdSlot placement="home_feed" />
                      </div>
                    )}
                  </div>
                  {expandedComments[post.id] && (
                    <CommentsInline contentId={post.id} contentType="post" contentOwnerId={post.userId} />
                  )}
                  </React.Fragment>
                );
              })()}

              {/* Remaining posts */}
              {filteredPosts.slice(1).map((post, idx) => {
                const overallIndex = idx + 1; // continue index after the first post
                const postVerified = resolveUserVerification(post.userId, post.userVerified);
                return (
                  <React.Fragment key={post.id}>
                  <div style={styles.postCard} className="post-card">
                    <PostViewTracker id={post.id} />
                    <div style={styles.postHeader}>
                      <ImageWithFallback 
                        src={(post.ownerType === 'page' ? post.pagePhoto : post.userPhoto) || undefined} 
                        alt="" 
                        style={styles.userAvatar}
                        className="user-avatar"
                        onClick={() => post.ownerType === 'page' && post.pageId ? navigate(`/page/${post.pageId}`) : navigate(getProfileUrl({ id: post.userId }))}
                      />
                      <div>
                        <strong 
                          style={styles.userName}
                          className="user-name"
                          onClick={() => post.ownerType === 'page' && post.pageId ? navigate(`/page/${post.pageId}`) : navigate(getProfileUrl({ id: post.userId }))}
                        >
                          {post.ownerType === 'page' ? (post.pageName || 'Page') : post.userName}
                          {postVerified && post.ownerType !== 'page' && <VerifiedBadge size={14} />}
                        </strong>
                        {(['pending','needs_review'].includes((post as any).moderation?.status)) && post.userId === (auth.currentUser?.uid || '') && (
                          <span style={styles.pendingBadge}>Pending review</span>
                        )}
                        <p style={styles.postTime}>
                          {post.type === 'repost' ? 'Reposted • ' : ''}
                          {post.createdAt?.toDate?.()?.toLocaleString?.() || (post.createdAt ? new Date(post.createdAt).toLocaleString() : 'Just now')}
                        </p>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <span style={styles.audienceBadge}>
                            {(((post as any).audience || 'public') === 'public') ? '🌍 Public' : '👥 Friends'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {post.type === 'repost' && post.original ? (
                      <div style={styles.repostWrapper}>
                        <div style={styles.repostHeader}>
                          Reposted from{' '}
                          <span
                            style={{ color: 'var(--color-primary-700)', cursor: 'pointer', fontWeight: 600 }}
                            onClick={() => navigate(getProfileUrl({ id: post.original!.userId }))}
                          >
                            {post.original.userName}
                          </span>
                        </div>
                        {post.original.content && (
                          <p style={styles.postContent}>{renderTextWithLinks(post.original.content)}</p>
                        )}
                        {post.original.imageURL && (
                          <MediaBox src={post.original.imageURL} type="image" alt="Original image" />
                        )}
                        {post.original.videoURL && (
                          <MediaBox src={post.original.videoURL} type="video" controls contain={true} />
                        )}
                      </div>
                    ) : post.type && (post.type === 'live_repost' || post.type === 'recording_repost') ? (
                      <div style={styles.repostWrapper}>
                        <div style={styles.repostHeader}>
                          {post.type === 'live_repost' ? 'Reposted Live Stream' : 'Reposted Recording'}
                        </div>
                        <div
                          style={{
                            position: 'relative',
                            border: '1px solid var(--border)',
                            borderRadius: 12,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            background: '#000'
                          }}
                          onClick={() => post.originalStreamId && navigate(`/live/${post.originalStreamId}`)}
                        >
                          {post.streamThumbnail ? (
  <img
    src={post.streamThumbnail}
    alt={post.title || 'Live stream thumbnail'}
    style={{
      width: '100%',
      display: 'block',
      maxHeight: 320,
      objectFit: 'cover',
    }}
  />
) : (
  <div
    style={{
      width: '100%',
      paddingTop: '56.25%',
      background: 'rgba(255,255,255,0.08)',
    }}
  />
)}
                          <div style={{ position: 'absolute', top: 8, left: 8, background: post.type === 'live_repost' ? '#DC2626' : '#3b82f6', color: '#fff', fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 8 }}>
                            {post.type === 'live_repost' ? 'LIVE' : 'RECORDED'}
                          </div>
                          {post.title && (
                            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0))', color: '#fff', padding: '12px 14px', fontSize: 14, fontWeight: 600 }}>
                              {post.title}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <>
                        <p style={styles.postContent}>{renderTextWithLinks(post.content)}</p>
                        {(hasMultiMedia(post) || post.imageURL) && (
                          <div style={{ position: 'relative' }} ref={(el) => { postContainerRefs.current[post.id] = el; if (el) el.dataset.postid = post.id; }}>
                            {hasMultiMedia(post)
                              ? <PostMediaGallery media={post.media || []} poster={post.thumbnailURL} postId={post.id} />
                              : <MediaBox src={post.imageURL || ''} type="image" alt="Post image" />}
                            {(post as any).bgMusic?.audioURL && (
                              <div style={{ marginTop: 0 }}>
                                <SignedAudio
                                  autoPlay
                                  loop
                                  playsInline
                                  controls={false}
                                  preload="none"
                                  src={(post as any).bgMusic.audioURL}
                                  muted={!!bgMuted[post.id]}
                                  ref={(el) => { if (el) { bgAudioRefs.current[post.id] = el; el.muted = !!bgMuted[post.id]; } }}
                                  style={{ display: 'none' }}
                                />
                              </div>
                            )}
                            {(post as any).bgMusic?.audioURL && (
                              <button
                                aria-label={bgMuted[post.id] ? 'Unmute background music' : 'Mute background music'}
                                onClick={() => toggleBgMute(post.id)}
                                style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: 20, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 2 }}
                              >
                                {bgMuted[post.id] ? '🔇' : '🔊'}
                              </button>
                            )}
                          </div>
                        )}
                        {!hasMultiMedia(post) && post.videoURL && (
                          <MediaBox src={post.videoURL} poster={post.thumbnailURL} type="video" controls contain={true} watchContent={{ type: 'post', id: post.id }} />
                        )}
                      </>
                    )}

                    <div style={{ ...styles.postActions }}>
                      <div style={postActionsRowStyle}>
                        <button
                          onClick={() => handleLikePost(post.id)}
                          style={actionButtonStyle}
                          className="action-button"
                        >
                          {post.likedByMe ? '❤️' : '🤍'} {post.likeCount || 0}
                        </button>
                        <button
                          onClick={() => { trackInteraction(post.id, 'comment'); setExpandedComments((m) => ({ ...m, [post.id]: !m[post.id] })); }}
                          style={actionButtonStyle}
                          className="action-button"
                        >
                          💬 {post.comments}
                        </button>
                        <button
                          onClick={() => handleShare(post.id)}
                          style={actionButtonStyle}
                          className="action-button"
                        >
                          🔗 Share
                        </button>
                        <button
                          onClick={() => toggleBookmark(post.id)}
                          style={actionButtonStyle}
                          className="action-button"
                        >
                          {savedPostIds[post.id] ? '✅ Saved' : '📌 Save'}
                        </button>
                        {auth.currentUser && post.userId === auth.currentUser.uid && post.type !== 'repost' && (
                          <>
                            <button
                              onClick={() => openEditPost(post)}
                              style={actionButtonStyle}
                              className="action-button"
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => handleDeletePost(post)}
                              style={actionButtonStyle}
                              className="action-button"
                            >
                              🗑️ Delete
                            </button>
                          </>
                        )}
                        {canModerate && auth.currentUser && post.userId !== auth.currentUser.uid && (
                          <button onClick={() => handleDeletePost(post)} style={{ ...actionButtonStyle, color: 'var(--color-error)' }} className="action-button">🛡️ Remove</button>
                        )}
                        {auth.currentUser && post.userId !== auth.currentUser.uid && post.type !== 'repost' && (
                          <>
                            <button
                              onClick={() => openRepostModal(post)}
                              style={actionButtonStyle}
                              className="action-button"
                            >
                              🔁 Repost
                            </button>
                            <button
                              onClick={() => setReportingPost({ id: post.id, userId: post.userId })}
                              style={{ ...actionButtonStyle, color: 'var(--color-error)' }}
                              className="action-button report-button"
                            >
                              🚩 Report
                            </button>
                          </>
                        )}
                      </div>
                      
                    </div>
                    {(((overallIndex) + 1) % 5 === 0) && (
                      <div key={`ad-${post.id}`} style={{ marginTop: 12 }}>
                        <AdSlot placement="home_feed" />
                      </div>
                    )}
                  </div>
                  {expandedComments[post.id] && (
                    <CommentsInline contentId={post.id} contentType="post" contentOwnerId={post.userId} />
                  )}
                  {overallIndex === 2 && trendingStrip}
                  </React.Fragment>
                );
              })}

            </>
          )}


              {filteredPosts.length < 3 && trendingStrip}


        </div>
      </main>

      {viewerOpen && (
        <StoriesViewer groups={storyGroups} startIndex={viewerStartIndex} onClose={() => setViewerOpen(false)} />
      )}

      {/* Inline comments – modal removed per UX request */}

      {reportingPost && (
        <ReportModal
          contentId={reportingPost.id}
          contentType="post"
          reportedUserId={reportingPost.userId}
          onClose={() => setReportingPost(null)}
        />
      )}
      {editingPost && (
        <>
          <div
            onClick={() => setEditingPost(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1200 }}
          />
          <div
            style={{
              position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
              width: '90%', maxWidth: 520, background: '#fff', borderRadius: 12,
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)', zIndex: 1201,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 16, borderBottom: '1px solid #E0E0E0' }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>Edit Post</h3>
              <button onClick={() => setEditingPost(null)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 16 }}>
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                placeholder="Update your post..."
                rows={4}
              />
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  onClick={submitEditPost}
                  style={{ padding: '10px 16px', borderRadius: 8, border: 'none', background: '#0B6B6F', color: '#fff', cursor: 'pointer' }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      {showRepostModal && repostTarget && (
        <>
          <div
            onClick={() => setShowRepostModal(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1200 }}
          />
          <div
            style={{
              position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
              width: '90%', maxWidth: 520, background: '#fff', borderRadius: 12,
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)', zIndex: 1201,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 16, borderBottom: '1px solid #E0E0E0' }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>Repost</h3>
              <button onClick={() => setShowRepostModal(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 16 }}>
              <div style={{ marginBottom: 10, fontSize: 13, color: '#5B6E6E' }}>
                Optional: add a comment to your repost
              </div>
              <textarea
                value={quoteText}
                onChange={(e) => setQuoteText(e.target.value)}
                placeholder="Say something about this..."
                rows={3}
              />
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  onClick={submitRepost}
                  disabled={postingRepost}
                  style={{ padding: '10px 16px', borderRadius: 8, border: 'none', background: '#0B6B6F', color: '#fff', cursor: 'pointer' }}
                >
                  {postingRepost ? 'Posting…' : 'Repost'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      {/* Video editor and quick trim for posts */}
      {showEditor && mediaFile && isVideoFile(mediaFile) && (
        <ReelEditorV2
          open={showEditor}
          videoFile={mediaFile}
          onClose={() => setShowEditor(false)}
          onSave={(blob) => {
            const edited = new File([blob], `${Date.now()}_post.webm`, { type: (blob as any).type || 'video/webm' });
            setMediaFile(edited);
            setShowEditor(false);
          }}
          maxDurationSeconds={MAX_POST_SECONDS}
          initialCaption={newPostContent}
          onCaptionChange={(c) => setNewPostContent(c)}
        />
      )}
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: 'var(--color-background-alt)',
  },
  contentArea: {
    paddingTop: '16px',
    marginLeft: '300px',
    marginRight: '340px',
    paddingLeft: '20px',
    paddingRight: '20px',
  },
  contentAreaMobile: {
    marginLeft: 0,
    marginRight: 0,
    paddingLeft: '12px',
    paddingRight: '12px',
    paddingTop: '8px',
  },
  main: {
    flex: 1,
    maxWidth: '800px',
    margin: '0 auto',
    padding: '0 0 40px 0',
  },
  mainMobile: {
    paddingBottom: 88,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '15px 30px',
    backgroundColor: 'var(--color-primary-700)',
    color: 'var(--color-text-inverse)',
    boxShadow: 'var(--shadow-sm)',
  },
  logo: {
    fontSize: '24px',
    fontWeight: 'bold',
    margin: 0,
  },
  nav: {
    display: 'flex',
    gap: '10px',
  },
  navButton: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    color: 'var(--color-text-inverse)',
    border: '1px solid var(--color-primary-300)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all var(--transition-fast)',
  },
  headerActions: {
    display: 'flex',
    gap: '10px',
  },
  profileButton: {
    padding: '8px 16px',
    backgroundColor: 'var(--color-primary-500)',
    color: 'var(--color-text-inverse)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all var(--transition-fast)',
  },
  logoutButton: {
    padding: '8px 16px',
    backgroundColor: 'var(--color-primary-300)',
    color: 'var(--color-text-primary)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all var(--transition-fast)',
  },
  progressWrap: {
    marginTop: 10,
    padding: '8px 10px',
    borderRadius: 10,
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
  },
  progressHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
    fontWeight: 600,
    color: 'var(--color-text-primary)'
  },
  progressPct: {
    fontVariantNumeric: 'tabular-nums',
    fontSize: 14,
    color: 'var(--color-primary-700)'
  },
  progressTrack: {
    height: 10,
    borderRadius: 999,
    background: 'linear-gradient(180deg, #E8F3F4, #D7ECEE)',
    overflow: 'hidden',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.06)'
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #17A2A9, #0B6B6F)',
    borderRadius: 999,
    transition: 'width 200ms ease',
  },
  createPost: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--spacing-lg)',
    marginBottom: 'var(--spacing-lg)',
    boxShadow: 'var(--shadow-base)',
    border: '1px solid var(--color-border)',
    transition: 'box-shadow var(--transition-base)',
  },
  createPostTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: 'var(--color-text-primary)',
    marginBottom: '10px',
  },
  textarea: {
    padding: 'var(--spacing-md)',
    fontSize: '16px',
    borderRadius: 'var(--radius-md)',
    border: '2px solid var(--color-border)',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    resize: 'vertical',
    marginBottom: '10px',
    transition: 'box-shadow var(--transition-fast), border-color var(--transition-fast)',
    color: 'var(--color-text-primary)',
    backgroundColor: 'var(--color-surface)',
  },
  createPostActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  createPostActionsMobile: {
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'flex-end',
  },
  audienceBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 999,
    border: '1px solid var(--color-border)',
    color: 'var(--color-text-muted)',
    fontSize: 12,
    background: 'var(--color-surface)',
  },
  createPostRow: {
    display: 'flex',
    gap: 'var(--spacing-md)',
    alignItems: 'flex-start',
    marginBottom: '10px',
  },
  createAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    objectFit: 'cover' as const,
    border: '2px solid var(--color-border)',
  },
  mediaLabel: {
    padding: '8px 16px',
    backgroundColor: 'var(--color-primary-50)',
    color: 'var(--color-text-primary)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all var(--transition-fast)',
    border: '1px solid var(--color-border)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '45%',
    display: 'inline-block',
  },
  fileInput: {
    display: 'none',
  },
  mediaActionsInline: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  smallButtonSecondary: {
    padding: '6px 10px',
    backgroundColor: 'var(--color-surface)',
    color: 'var(--color-text-primary)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontSize: '12px',
  },
  smallButtonDanger: {
    padding: '6px 10px',
    backgroundColor: '#b71c1c',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontSize: '12px',
  },
  postButton: {
    padding: '10px 24px',
    backgroundColor: 'var(--color-primary-500)',
    color: 'var(--color-text-inverse)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    transition: 'all var(--transition-fast)',
  },
  storiesRail: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--spacing-md)',
    marginBottom: 'var(--spacing-base)',
    boxShadow: 'var(--shadow-sm)',
    border: '1px solid var(--color-border)',
  },
  storiesScroll: {
    display: 'flex',
    gap: 'var(--spacing-md)',
    overflowX: 'auto',
    paddingBottom: '4px',
  },
  storyItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: '68px',
    cursor: 'pointer',
  },
  storyAvatar: {
    width: '54px',
    height: '54px',
    borderRadius: '50%',
    objectFit: 'cover' as const,
    transition: 'transform var(--transition-fast)'
  },
  suggestBox: {
    marginTop: 6,
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-lg)',
    padding: 8,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 6,
    maxHeight: 220,
    overflowY: 'auto' as const,
  },
  suggestHeader: {
    fontSize: 12,
    color: 'var(--color-text-muted)',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
  },
  suggestItem: {
    padding: '8px 6px',
    borderRadius: 8,
    cursor: 'pointer',
    border: '1px solid transparent',
  },
  suggestEmpty: {
    padding: '8px 6px',
    fontSize: 12,
    color: 'var(--color-text-muted)'
  },
  storyRing: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    padding: '2px',
    background: 'conic-gradient(#f58529, #feda77, #dd2a7b, #8134af, #515bd4, #f58529)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
  },
  storyInner: {
    height: '100%',
    borderRadius: '50%',
    padding: '3px',
    background: 'var(--color-surface)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  storyName: {
    fontSize: '11px',
    color: 'var(--color-text-primary)',
    marginTop: '6px',
    maxWidth: '68px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  addStory: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 18,
    height: 18,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #26c6da, #0B6B6F)',
    color: 'var(--color-text-inverse)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    border: '2px solid var(--color-surface)',
  },
  quickLinks: {
    display: 'flex',
    gap: 'var(--spacing-md)',
    marginBottom: 'var(--spacing-base)',
  },
  quickLinksMobile: {
    display: 'flex',
    flexDirection: 'row',
    gap: '12px',
  },
  linkCard: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-lg)',
    padding: '14px',
    boxShadow: 'var(--shadow-sm)',
    border: '1px solid var(--color-border)',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
    transition: 'all var(--transition-fast)',
  },
  linkCardMobile: {
    flex: 1,
    minWidth: 0,
    justifyContent: 'center',
    padding: '12px',
  },
  trendingSection: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--spacing-md)',
    marginBottom: 'var(--spacing-md)',
    boxShadow: 'var(--shadow-sm)',
    border: '1px solid var(--color-border)',
  },
  trendingTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: 'var(--color-text-primary)',
    marginBottom: '10px',
    marginTop: 0,
  },
  trendingGrid: {
    display: 'flex',
    gap: '12px',
    overflowX: 'auto',
    scrollSnapType: 'x mandatory',
    WebkitOverflowScrolling: 'touch',
    paddingBottom: 4,
  },
  trendingCard: {
    scrollSnapAlign: 'start',
    minWidth: 0,
    backgroundColor: 'var(--color-gray-50)',
    borderRadius: 'var(--radius-lg)',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all var(--transition-base)',
    boxShadow: 'var(--shadow-sm)',
    border: '1px solid var(--color-border)',
  },
  trendingMediaContainer: {
    position: 'relative',
    // Let the media define the height; we'll enforce aspect ratio per-type inline when needed
    backgroundColor: 'var(--color-gray-900)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  trendingMedia: {
    width: '100%',
    height: 'auto',
    objectFit: 'contain' as const,
  },
  trendingBadge: {
    position: 'absolute',
    top: '10px',
    left: '10px',
    backgroundColor: 'rgba(23, 162, 169, 0.95)',
    color: 'var(--color-text-inverse)',
    padding: '3px 8px',
    borderRadius: 'var(--radius-full)',
    fontSize: '11px',
    fontWeight: '500',
    backdropFilter: 'blur(8px)',
  },
  trendingContent: {
    padding: 'var(--spacing-sm)',
  },
  trendingItemTitle: {
    fontSize: '12px',
    fontWeight: 'bold',
    color: 'var(--color-text-primary)',
    marginBottom: '6px',
    marginTop: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  trendingCaption: {
    fontSize: '11px',
    color: 'var(--color-text-secondary)',
    marginBottom: '6px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  trendingMeta: {
    display: 'flex',
    gap: 'var(--spacing-sm)',
    fontSize: '11px',
    color: 'var(--color-text-secondary)',
  },
  feed: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  emptyFeedContainer: {
    textAlign: 'center' as const,
    padding: '40px',
  },
  emptyFeed: {
    textAlign: 'center' as const,
    color: 'var(--color-text-secondary)',
    fontSize: '16px',
    margin: 0,
  },
  postCard: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--spacing-lg)',
    boxShadow: 'var(--shadow-base)',
    border: '1px solid var(--color-border)',
    transition: 'box-shadow var(--transition-base)',
  },
  postHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-md)',
    marginBottom: 'var(--spacing-md)',
  },
  userAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    objectFit: 'cover' as const,
    cursor: 'pointer',
    transition: 'transform var(--transition-fast)',
    border: '2px solid var(--color-border-light)',
  },
  userName: {
    fontSize: '16px',
    color: 'var(--color-text-primary)',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'color var(--transition-fast)',
  },
  pendingBadge: {
    display: 'inline-block',
    marginLeft: '8px',
    padding: '2px 8px',
    backgroundColor: '#FFF7CC',
    color: '#8B6F00',
    borderRadius: 'var(--radius-full)',
    fontSize: '11px',
    fontWeight: '500',
  },
  postTime: {
    fontSize: '12px',
    color: 'var(--color-text-secondary)',
    margin: 0,
  },
  postContent: {
    fontSize: '16px',
    color: 'var(--color-text-primary)',
    marginBottom: 'var(--spacing-md)',
    lineHeight: '1.6',
  },
  // postMedia removed: handled by MediaBox component
  postActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    paddingTop: '10px',
    borderTop: '1px solid var(--color-border)'
  },
  postActionsRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  postActionsRowMobile: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'nowrap',
    overflowX: 'auto',
  },
  actionButton: {
    padding: '6px 8px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '11px',
    color: 'var(--color-text-secondary)',
    transition: 'all var(--transition-fast)',
    fontWeight: '500',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    borderRadius: 10,
  },
  actionButtonMobile: {
    justifyContent: 'center',
    border: '1px solid var(--color-border-light)',
    padding: '8px 10px',
    fontSize: '12px',
    backgroundColor: 'var(--color-surface)',
    borderRadius: 12,
  },
  reportButton: {
    padding: '6px 10px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '11px',
    color: 'var(--color-error)',
    transition: 'all var(--transition-fast)',
  },
  reportRow: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  repostWrapper: {
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    padding: '10px',
    backgroundColor: 'var(--color-surface)',
  },
  repostHeader: {
    fontSize: '12px',
    color: 'var(--color-text-secondary)',
    marginBottom: '6px',
  },
  liveStreamsSection: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--spacing-lg)',
    marginBottom: '15px',
    boxShadow: 'var(--shadow-base)',
    border: '1px solid var(--color-border)',
  },
  liveStreamsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 'var(--spacing-md)',
  },
  liveStreamsTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: 'var(--color-text-primary)',
  },
  seeAllButton: {
    padding: '6px 14px',
    backgroundColor: 'transparent',
    color: 'var(--color-primary-500)',
    border: '1px solid var(--color-primary-500)',
    borderRadius: 'var(--radius-md)',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all var(--transition-fast)',
  },
  liveStreamsScroll: {
    display: 'flex',
    gap: '12px',
    overflowX: 'auto' as const,
    paddingBottom: '8px',
  },
  liveStreamCard: {
    minWidth: '200px',
    backgroundColor: 'var(--color-background-alt)',
    borderRadius: 'var(--radius-md)',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'transform var(--transition-fast)',
  },
  liveStreamThumbnail: {
    position: 'relative' as const,
    height: '120px',
    backgroundColor: '#000',
  },
  liveStreamImage: {
    height: '100%',
    objectFit: 'cover' as const,
  },
  liveBadge: {
    position: 'absolute' as const,
    top: '8px',
    left: '8px',
    backgroundColor: 'var(--color-error)',
    color: 'white',
    padding: '4px 8px',
    borderRadius: 'var(--radius-sm)',
    fontSize: '11px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  liveDot: {
    fontSize: '8px',
    animation: 'pulse 1.5s infinite',
  },
  liveViewers: {
    position: 'absolute' as const,
    bottom: '8px',
    right: '8px',
    backgroundColor: 'rgba(0,0,0,0.7)',
    color: 'white',
    padding: '4px 8px',
    borderRadius: 'var(--radius-sm)',
    fontSize: '11px',
    fontWeight: '500',
  },
  liveStreamInfo: {
    display: 'flex',
    gap: '8px',
    padding: '10px',
    alignItems: 'center',
  },
  liveStreamAvatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    objectFit: 'cover' as const,
  },
  liveStreamDetails: {
    flex: 1,
    minWidth: 0,
  },
  liveStreamTitle: {
    fontSize: '12px',
    fontWeight: '600',
    color: 'var(--color-text-primary)',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  liveStreamUser: {
    fontSize: '12px',
    color: 'var(--color-text-secondary)',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};

// Helper to get video duration from a File
function getVideoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    try {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        try { URL.revokeObjectURL(video.src); } catch {}
        resolve(video.duration || 0);
      };
      video.onerror = () => reject(new Error('Failed to load video metadata'));
      video.src = URL.createObjectURL(file);
    } catch (e) {
      reject(e);
    }
  });
}






















